// ============================================
// BANNER DA RÉGUA DE INADIMPLÊNCIA
//
// Degraus intermediários da régua, antes do bloqueio:
//   warning  → venceu hoje, aviso discreto
//   overdue  → vencido dentro da carência, aviso persistente com prazo
//
// Só aparece para quem pode agir (Admin Master). Recepção e profissionais não
// precisam ser expostos à situação financeira da clínica.
// ============================================

import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { WarningAmberRounded as WarningIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { PlatformBillingState } from '../services/platformBillingStore';
import { isKlinviaNativeApp } from '../utils/nativeApp';

const AMBER_BG = '#FEF3C7';
const AMBER_BORDER = '#FCD34D';
const AMBER_TEXT = '#92400E';
const RED_BG = '#FEE2E2';
const RED_BORDER = '#FCA5A5';
const RED_TEXT = '#991B1B';

function formatDate(ymd: string | null): string {
  if (!ymd) return '—';
  return new Date(`${ymd.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}

const PlatformBillingBanner: React.FC<{ state: PlatformBillingState; canSee: boolean }> = ({
  state,
  canSee,
}) => {
  const navigate = useNavigate();

  if (!canSee || !state.configured || isKlinviaNativeApp()) return null;
  if (state.billingState !== 'warning' && state.billingState !== 'overdue') return null;

  const isOverdue = state.billingState === 'overdue';

  const message = isOverdue
    ? `Mensalidade em atraso há ${state.daysOverdue} ${state.daysOverdue === 1 ? 'dia' : 'dias'}.${
        state.graceUntil ? ` O acesso é suspenso em ${formatDate(state.graceUntil)}.` : ''
      }`
    : 'A mensalidade da plataforma vence hoje.';

  return (
    <Box
      role="status"
      sx={{
        px: { xs: 2, md: 3 },
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: isOverdue ? RED_BORDER : AMBER_BORDER,
        bgcolor: isOverdue ? RED_BG : AMBER_BG,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <WarningIcon sx={{ fontSize: 20, color: isOverdue ? RED_TEXT : AMBER_TEXT, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{ color: isOverdue ? RED_TEXT : AMBER_TEXT, fontWeight: 600, lineHeight: 1.4 }}
          >
            {message}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant={isOverdue ? 'contained' : 'text'}
          endIcon={<OpenInNewIcon sx={{ '&&': { fontSize: 16 } }} />}
          onClick={() => navigate('/platform-payments')}
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            borderRadius: 1.5,
            ...(isOverdue
              ? { bgcolor: RED_TEXT, '&:hover': { bgcolor: '#7F1D1D' } }
              : { color: AMBER_TEXT }),
          }}
        >
          Ver fatura
        </Button>
      </Stack>
    </Box>
  );
};

export default PlatformBillingBanner;
